import api from "../../axiosSetup";
import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "@/redux/store"; // adjust the path as needed
import { IRole } from "../../../types/IRole";
import { IUser } from "../../../types/IUser";
import { getApiClient } from "@/utils/getApiClient";

// Define the Initial State
type UsersState = {
  users: IUser[];
  userMutable: IUser | null,
  loading: boolean;
  error: string | null;
};

const initialState: UsersState = {
  users: [],
  userMutable: null,
  loading: false,
  error: null,
};

// Create the users slice
const usersSlice = createSlice({
  name: "users",
  initialState,
  reducers: {
    setUsers: (state, action: PayloadAction<IUser[]>) => {
      state.users = action.payload;
    },
    addUser: (state, action: PayloadAction<IUser>) => {
      state.users.push(action.payload);
    },
    removeUser: (state, action: PayloadAction<string>) => {
      state.users = state.users.filter(user => user._id !== action.payload);
    },
    setUserMutable: (state, action: PayloadAction<IUser>) => {
      state.userMutable = action.payload;
    },
    syncUserRoles: (state, action: PayloadAction<{ roleId: string; updatedRole: IRole }>) => {
      const { roleId, updatedRole } = action.payload;

      state.users = state.users.map((user) =>
        user.roleId === roleId
          ? { ...user, role: updatedRole }
          : user
      );
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchReserveUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchReserveUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.users = action.payload;
      })
      .addCase(fetchReserveUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Create User
      .addCase(createUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createUser.fulfilled, (state, action) => {
        state.loading = false;
        state.users.push(action.payload);
      })
      .addCase(createUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Update User
      .addCase(updateUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateUser.fulfilled, (state, action: PayloadAction<IUser>) => {
        state.loading = false;
        state.users = state.users.map((user) =>
          user._id === action.payload._id ? action.payload : user
        );
      })
      // .addCase(updateUser.rejected, (state, action) => {
      //   state.loading = false;
      //   state.error = action.payload || "Failed to update user";
      // })
      .addCase(updateUser.rejected, (state, action) => {
        state.loading = false;
        // Always store a string
        state.error =
          typeof action.payload === 'string'
            ? action.payload
            : (action.payload as any)?.message || 'Failed to update user';
      })
      // Delete User (1/3)
      .addCase(deleteUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteUser.fulfilled, (state, action: PayloadAction<IUser>) => {
        state.loading = false;
        state.users = state.users.filter(user => user._id !== action.payload._id);
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to update user";
      });
  },
});

// Async thunk to fetch users from API
export const fetchReserveUsers = createAsyncThunk<IUser[], string>(
  "users/fetchUsers",
  async (reserveId, { rejectWithValue }) => { // ✅ Accept reserveId as the first argument
    try {
      const client = getApiClient();

      const response = await client.get(`/api/users/by-reserve?reserveId=${reserveId}`);
      return response.data.users; // ✅ Ensure you're returning `users` from API response
    } catch (error) {
      return rejectWithValue("Failed to fetch users");
    }
  }
);

export const createUser = createAsyncThunk<
  IUser, // ✅ Return type (User)
  IUser, // ✅ Argument type (New User)
  { rejectValue: string } // ✅ Error type
>(
  "users/createUser",
  async (user, { rejectWithValue }) => {
    try {
      const response = await api.post("/api/users", { user }); // ✅ Send user data in body
      return response.data.user; // ✅ Return user from API response
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || "Failed to create user");
    }
  }
);

type UpdateUserReject = {
  message?: string;
  code?: string | number;
  keyPattern?: { email?: number };
} | string;

export const updateUser = createAsyncThunk<
  IUser & { role?: IRole | null },
  { userId: string; user: Partial<IUser>; isAdminPortal?: boolean },
  { rejectValue: UpdateUserReject; state: RootState }
>(
  'users/updateUser',
  async ({ userId, user, isAdminPortal }, { rejectWithValue, getState }) => {
    try {
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      const res = await api.put(`/api/users/${userId}`, {
        user,
        timeZone,
        isAdminPortal: !!isAdminPortal,
      });

      if (res.data?.requireRelogin) {
        return rejectWithValue({
          code: 'RELOGIN_REQUIRED' as const,
          message: res.data.message ?? 'Password updated. Please log in again.',
        });
      }

      const roles = getState().reserveReducer.roles;
      const matchedRole = roles.find(r => r._id === res.data.user.roleId) ?? null;

      return { ...res.data.user, role: matchedRole };
    } catch (err: any) {
      const data = err?.response?.data ?? {};
      const rawMsg = data.message;
      const message =
        typeof rawMsg === 'string'
          ? rawMsg
          : (rawMsg?.errmsg || err?.message || 'Failed to update user');

      return rejectWithValue({
        message,
        code: data.code ?? err?.code,
        keyPattern: data.keyPattern,
      });
    }
  }
);
// export const updateUser = createAsyncThunk<
//   IUser & { role?: IRole | null },
//   { userId: string; user: Partial<IUser> },
//   { rejectValue: UpdateUserReject; state: RootState }
// >(
//   'users/updateUser',
//   async ({ userId, user }, { rejectWithValue, getState }) => {
//     try {
//       const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
//       const res = await api.put(`/api/users/${userId}`, { user, timeZone });

//       if (res.data?.requireRelogin) {
//         return rejectWithValue({
//           code: 'RELOGIN_REQUIRED' as const,
//           message: res.data.message ?? 'Password updated. Please log in again.',
//         });
//       }

//       const roles = getState().reserveReducer.roles;
//       const matchedRole = roles.find(r => r._id === res.data.user.roleId) ?? null;

//       return { ...res.data.user, role: matchedRole };
//     } catch (err: any) {
//       const data = err?.response?.data ?? {};
//       const rawMsg = data.message;
//       const message =
//         typeof rawMsg === 'string'
//           ? rawMsg
//           : (rawMsg?.errmsg || err?.message || 'Failed to update user');

//       return rejectWithValue({
//         message,
//         code: data.code ?? err?.code,
//         keyPattern: data.keyPattern,
//       });
//     }
//   }
// );

export const deleteUser = createAsyncThunk<
  IUser, // On success, return deleted user
  { userId: string },
  { rejectValue: any }
>(
  "users/deleteUser",
  async ({ userId }, { rejectWithValue }) => {
    try {
      const response = await api.delete(`/api/users/${userId}`);
      return response.data.user;
    } catch (error: any) {
      // Forward message + bcrs if user is in BCRs
      const customError = {
        message: error.response?.data?.message || "Failed to delete user",
        bcrs: error.response?.data?.bcrs || [],
        needsConfirmation: error.response?.data?.needsConfirmation || false,
      };
      return rejectWithValue(customError);
    }
  }
);

export const deleteCheckUser = createAsyncThunk<
  IUser, // On success, return deleted user
  { userId: string },
  { rejectValue: any }
>(
  "users/deleteUser",
  async ({ userId }, { rejectWithValue }) => {
    try {
      const response = await api.delete(`/api/users/${userId}/check-or-delete`);
      return response.data.user;
    } catch (error: any) {
      // Forward message + bcrs if user is in BCRs
      const customError = {
        message: error.response?.data?.message || "Failed to delete user",
        bcrs: error.response?.data?.bcrs || [],
        needsConfirmation: error.response?.data?.needsConfirmation || false,
      };
      return rejectWithValue(customError);
    }
  }
);

export const forceDeleteUser = createAsyncThunk<
  IUser, // On success, return deleted user
  { userId: string },
  { rejectValue: any }
>(
  "users/deleteUser",
  async ({ userId }, { rejectWithValue }) => {
    try {
      const response = await api.post(`/api/users/force-delete/${userId}`);
      return response.data.user;
    } catch (error: any) {
      // Forward message + bcrs if user is in BCRs
      const customError = {
        message: error.response?.data?.message || "Failed to delete user",
        bcrs: error.response?.data?.bcrs || [],
        needsConfirmation: error.response?.data?.needsConfirmation || false,
      };
      return rejectWithValue(customError);
    }
  }
);

export const { setUsers, addUser, removeUser, setUserMutable, syncUserRoles } = usersSlice.actions;
export default usersSlice.reducer;
